import { type ReactNode, type Ref } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { HelpTip } from './HelpTip'

export interface FieldShellProps {
  labelId?: string | undefined
  containerRef?: Ref<HTMLDivElement> | undefined
  /** Azione (pulsante) in coda al campo, dopo l'icona. */
  trailingAction?: ReactNode | undefined
  id: string
  label: string
  labelHidden: boolean | undefined
  variant: 'outlined' | 'filled'
  supportingText: ReactNode | undefined
  error: string | undefined
  disabled: boolean | undefined
  floated: boolean
  leadingIcon: string | undefined
  trailingIcon: string | undefined
  prefix: ReactNode | undefined
  suffix: ReactNode | undefined
  multiline: boolean
  className: string | undefined
  describedBy: string | undefined
  children: ReactNode
}

export function FieldShell(p: FieldShellProps) {
  const hasMessage = Boolean(p.error || p.supportingText)
  // Senza errore la spiegazione del campo sta nel "?" accanto all'etichetta (resta letta dagli screen reader).
  const helpOnly = !p.error && Boolean(p.supportingText) && !p.labelHidden
  return (
    <div
      className={cx(
        'md-field',
        `md-field--${p.variant}`,
        p.error && 'md-field--error',
        p.disabled && 'md-field--disabled',
        p.floated && 'md-field--floated',
        p.labelHidden && 'md-field--no-label',
        p.multiline && 'md-field--multiline',
        p.leadingIcon && 'md-field--leading',
        p.trailingIcon && 'md-field--trailing',
        p.className,
      )}
    >
      <div className="md-field__container" ref={p.containerRef}>
        {p.leadingIcon ? <Icon name={p.leadingIcon} size={24} className="md-field__icon md-field__icon--leading" /> : null}
        {p.prefix ? <span className="md-field__affix md-field__affix--prefix">{p.prefix}</span> : null}
        <div className="md-field__control">
          {p.children}
          <label id={p.labelId} htmlFor={p.id} className={cx('md-field__label', p.labelHidden && 'md-sr-only')}>
            {p.label}
          </label>
          {helpOnly ? <span className="md-field__help"><HelpTip>{p.supportingText}</HelpTip></span> : null}
        </div>
        {p.suffix ? <span className="md-field__affix md-field__affix--suffix">{p.suffix}</span> : null}
        {p.trailingIcon ? <Icon name={p.trailingIcon} size={24} className="md-field__icon md-field__icon--trailing" /> : null}
        {p.trailingAction}
        <span className="md-field__indicator" aria-hidden="true" />
      </div>
      {hasMessage ? (
        <p id={p.describedBy} className={cx('md-field__support', helpOnly && 'md-sr-only')} role={p.error ? 'alert' : undefined}>
          {p.error ? <Icon name="error" size={18} /> : null}
          <span>{p.error ?? p.supportingText}</span>
        </p>
      ) : null}
    </div>
  )
}
